import React, { useState } from "react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { ChartCard } from "@/components/ChartCard";
import AiInsightsPanel from "@/components/AiInsightsPanel";
import DrillDownModal from "@/components/DrillDownModal";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useFilters } from "@/state/FilterContext";
import { formatTRY, formatNumber } from "@/lib/api";
import { CHART_PALETTE, SAP_BLUE } from "@/lib/palette";
import { Trophy, AlertTriangle } from "lucide-react";

export default function CarrierPerformance() {
  const { data } = useDashboardData("/dashboard/carrier-performance");
  const list = data || [];
  const { meta } = useFilters();
  const [drill, setDrill] = useState({ open: false, title: "", overrides: {} });

  const carCode = (name) => meta.carriers?.find((c) => c.name === name)?.code || name;
  const openCarrierDrill = (name) =>
    setDrill({ open: true, title: `${name} – Sevkiyatlar`, overrides: { carrier: carCode(name) } });

  const best = [...list].sort((a, b) => b.on_time_pct - a.on_time_pct)[0];
  const worst = [...list].sort((a, b) => a.on_time_pct - b.on_time_pct)[0];

  return (
    <div className="space-y-6">
      <AiInsightsPanel />

      {/* Winners/losers row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div data-testid="carrier-best" className="bg-white rounded-lg border border-slate-200 p-5 flex items-center gap-4 fade-up">
          <div className="w-12 h-12 rounded-md bg-emerald-50 flex items-center justify-center">
            <Trophy className="w-6 h-6 text-emerald-600" />
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold">En Yüksek Zamanında Teslimat</div>
            <div className="font-display text-xl font-bold text-slate-900 mt-0.5">{best?.carrier || "—"}</div>
            <div className="text-sm text-slate-600 mt-0.5 tabular-nums">
              %{formatNumber(best?.on_time_pct ?? 0)} · {formatNumber(best?.shipments ?? 0)} sevkiyat
            </div>
          </div>
        </div>
        <div data-testid="carrier-worst" className="bg-white rounded-lg border border-slate-200 p-5 flex items-center gap-4 fade-up">
          <div className="w-12 h-12 rounded-md bg-rose-50 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-rose-600" />
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold">Geliştirme Fırsatı</div>
            <div className="font-display text-xl font-bold text-slate-900 mt-0.5">{worst?.carrier || "—"}</div>
            <div className="text-sm text-slate-600 mt-0.5 tabular-nums">
              %{formatNumber(worst?.on_time_pct ?? 0)} · {formatTRY(worst?.cost_per_ton ?? 0)} / ton
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard testid="chart-carrier-otd" title="Zamanında Teslimat Oranı" subtitle="Nakliyeci bazında performans karşılaştırması">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={list} layout="vertical" margin={{ left: 20, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `%${v}`} tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis type="category" dataKey="carrier" width={130} tick={{ fontSize: 12, fill: "#334155" }} />
              <Tooltip
                content={({ active, payload, label }) =>
                  !active || !payload?.length ? null : (
                    <div className="bg-white border border-slate-200 rounded-md shadow-md px-3 py-2 text-xs">
                      <div className="font-semibold text-slate-800 mb-1">{label}</div>
                      <div className="text-slate-600 tabular-nums">Zamanında: %{payload[0].value}</div>
                    </div>
                  )
                }
              />
              <Bar dataKey="on_time_pct" radius={[0, 4, 4, 0]}
                cursor="pointer"
                onClick={(d) => d?.carrier && openCarrierDrill(d.carrier)}>
                {list.map((r, i) => (
                  <Cell key={i} fill={r.on_time_pct >= 90 ? "#10B981" : r.on_time_pct >= 80 ? SAP_BLUE : "#F59E0B"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard testid="chart-carrier-radar" title="Çok Boyutlu Performans" subtitle="Zamanında %, Verimlilik, Hacim Payı">
          <ResponsiveContainer width="100%" height={320}>
            <RadarChart data={list}>
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis dataKey="carrier" tick={{ fontSize: 11, fill: "#334155" }} />
              <PolarRadiusAxis tick={{ fontSize: 10, fill: "#94A3B8" }} angle={90} />
              <Radar name="Zamanında %" dataKey="on_time_pct" stroke={SAP_BLUE} fill={SAP_BLUE} fillOpacity={0.35} />
              <Radar name="Verimlilik" dataKey="efficiency" stroke="#00B0FF" fill="#00B0FF" fillOpacity={0.25} />
              <Radar name="Hacim Payı" dataKey="volume_share" stroke="#1E293B" fill="#1E293B" fillOpacity={0.15} />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <ChartCard testid="table-carrier-kpi" title="Nakliyeci Performans Tablosu" subtitle="Tüm metrikler tek bakışta">
        <div className="overflow-x-auto -mx-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.08em] text-slate-500 font-semibold bg-slate-50">
                <th className="p-3 pl-5">Nakliyeci</th>
                <th className="p-3 text-right">Sevkiyat</th>
                <th className="p-3 text-right">Toplam Maliyet</th>
                <th className="p-3 text-right">Maliyet / Ton</th>
                <th className="p-3 text-right">Zamanında</th>
                <th className="p-3 text-right pr-5">Hacim Payı</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r, i) => (
                <tr key={r.carrier} data-testid={`carrier-row-${i}`}
                  onClick={() => openCarrierDrill(r.carrier)}
                  className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors cursor-pointer">
                  <td className="p-3 pl-5 font-semibold text-slate-800">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: CHART_PALETTE[i % CHART_PALETTE.length] }} />
                      {r.carrier}
                    </span>
                  </td>
                  <td className="p-3 text-right text-slate-700 tabular-nums">{formatNumber(r.shipments)}</td>
                  <td className="p-3 text-right text-slate-900 font-semibold tabular-nums">{formatTRY(r.cost)}</td>
                  <td className="p-3 text-right text-slate-700 tabular-nums">{formatTRY(r.cost_per_ton)}</td>
                  <td className="p-3 text-right tabular-nums">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                      r.on_time_pct >= 90 ? "bg-emerald-50 text-emerald-700" :
                      r.on_time_pct >= 80 ? "bg-[#E5F0FA] text-[#0054E3]" : "bg-amber-50 text-amber-700"
                    }`}>
                      %{formatNumber(r.on_time_pct)}
                    </span>
                  </td>
                  <td className="p-3 pr-5 text-right text-slate-700 tabular-nums">%{formatNumber(r.volume_share)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      <DrillDownModal
        open={drill.open}
        onOpenChange={(o) => setDrill((d) => ({ ...d, open: o }))}
        title={drill.title}
        overrides={drill.overrides}
      />
    </div>
  );
}
